import React, { useState, useEffect, Fragment } from "react";
import { Button, Input, Modal, ModalBody, ModalHeader } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { CircularProgress } from "../../UI/CircularProgress/CircularProgress";
import { Prompt } from "react-router-dom";

import { Row, Col } from "../../UI/Customstrap/Customstrap";
import { Icons } from "../../UI/Icon/Icon";
import { Container } from "../../UI/Container/Container";
import { FormattedNumber } from "../../FormattedNumber/FormattedNumber";
import { AssetsRisksDoghnut, AssetRiskList } from "../Charts/Chart";
import { getCurrentAccount, getSavedPersonInfo } from "../../../reducers/selectors";
import { FEATURE, isActionAllowed, showToast } from "../../../utils/helper";
import { askPrice } from "../../../services/api/inout";
import { getAssetAttachment } from "../../../services/api/entities";

import style from "./AssetClass.scss";
import { getBankAccountBalance } from "../../../services/api/accounts";
import { BankAccountsSelector } from "../../BankAccountsSelector/BankAccountsSelector";
import { modes } from "../../Invest/FundsTable/FundsAssetTable/FundsAssetTable";

export const AssetClass = ({ assetClasses = [], portfolioTotal, activeCurrency, fx }) => {
  const [activeClass, setActiveClass] = useState(0);
  const [activeSubAssetClass, setActiveSubAssetClass] = useState(null);

  const handleChangeActiveClass = (idx) => {
    setActiveClass(idx);
  };

  return (
    <Row>
      <Col>
        <Row className={style.assetClassRow}>
          {assetClasses.map((assetClass, index) => (
            <Col className={style.buttonCol} key={assetClass.id}>
              <Button
                onClick={() => handleChangeActiveClass(index)}
                className={`${style.button} ${index === activeClass ? style.active : null}`}
              >
                {assetClass.name}
              </Button>
            </Col>
          ))}
        </Row>
        <Row>
          <Col xs={12}>
            <hr className={style.separator} />
          </Col>
        </Row>
        <Row>
          <Col>
            {/* Un row por cada sub asset class que tendrá un Link a la página siguiente */}
            {assetClasses.length > 0 &&
              assetClasses[activeClass].subAssetClass.map((subAssetClass) => (
                <Row key={subAssetClass.id}>
                  <Col xs={12}>
                    <Button
                      className={style.subAssetClass}
                      onClick={() => setActiveSubAssetClass(subAssetClass)}
                    >
                      <span>{subAssetClass.name}</span>
                      <div className={style.counterWrapper}>
                        <span className={style.counter}>{subAssetClass.asset.length}</span>
                        <Icons.ArrowRightIcon size="sm" />
                      </div>
                    </Button>
                  </Col>
                </Row>
              ))}
          </Col>
        </Row>
      </Col>
      <Prompt
        when={activeSubAssetClass}
        message={(location) => {
          // Ejecutar la función personalizada antes de la navegación hacia atrás
          setActiveSubAssetClass(null);

          // No permitir la navegación hacia atrás
          return false;
        }}
      />
      {activeSubAssetClass && (
        <SubAssetClassDisplay
          activeCurrency={activeCurrency}
          fx={fx}
          subAssetClass={activeSubAssetClass}
          portfolioTotal={portfolioTotal}
          toggle={() => setActiveSubAssetClass(null)}
        />
      )}
    </Row>
  );
};

const SubAssetClassDisplay = ({ subAssetClass, portfolioTotal, toggle, activeCurrency, fx }) => {
  const [totalMarketValue, setTotalMarketValue] = useState(0);
  const [totalMarketValueOneValue, setTotalMarketValueOneValue] = useState(0);
  const [totalInvestmentCost, setTotalInvestmentCost] = useState(0);
  const [totalInvestmentCostOneValue, setTotalInvestmentCostOneValue] = useState(0);
  const [totalAbsoluteReturn, setTotalAbsoluteReturn] = useState(0);
  const [totalNetReturn, setTotalNetReturn] = useState(0);
  const [totalNetReturnOneValue, setTotalNetReturnOneValue] = useState(0);
  const [totalYTM, setTotalYTM] = useState(0);
  const [totalPortfolio, setTotalPortfolio] = useState(0);
  const [sell, setSell] = useState({ open: false, asset: null, total: 0 });
  const currentAccount = useSelector(getCurrentAccount);

  // prevenir ir pa tras
  const isSubAssetFund = subAssetClass.asset.filter((asset) => asset.form == "Mutual Funds").length > 0;

  useEffect(() => {
    let totalMarketValue = 0;
    let totalMarketValueOneValue = 0;
    let totalInvestmentCost = 0;
    let totalInvestmentCostOneValue = 0;
    let totalAbsoluteReturn = 0;
    let totalNetReturn = 0;
    let totalNetReturnOneValue = 0;
    let totalYTM = 0;
    let totalPortfolio = 0;

    subAssetClass.asset.forEach((asset) => {
      let investCost = (+asset.NetReturn - (+asset.valuetotal + +asset.realizePL)) * -1;
      let investCostUSD = ((+asset.NetReturn - (+asset.valuetotal + +asset.realizePL)) * -1) / fx;
      let netReturnUSD = asset.NetReturn / fx;
      if (asset.emissionCurrencyISO === "PYG") {
        totalMarketValue += parseFloat(asset.totalUSDWithCoupon);
      } else {
        totalMarketValue += parseFloat(asset.totalOriginCurrencyWithCoupon);
      }
      if (asset.emissionCurrencyISO === "USD") {
        totalMarketValueOneValue += parseFloat(asset.totalUSDWithCoupon);
      } else {
        totalMarketValueOneValue += parseFloat(asset.totalOriginCurrencyWithCoupon);
      }

      if (asset.emissionCurrencyISO === "PYG") {
        totalInvestmentCost += parseFloat(investCostUSD);
      } else {
        totalInvestmentCost += parseFloat(investCost);
      }
      if (asset.emissionCurrencyISO === "USD") {
        totalInvestmentCostOneValue += parseFloat(investCostUSD);
      } else {
        totalInvestmentCostOneValue += parseFloat(investCost);
      }

      if (asset.emissionCurrencyISO === "PYG" || asset.emissionCurrencyId === "0") {
        totalNetReturn += parseFloat(netReturnUSD);
      } else {
        totalNetReturn += parseFloat(asset.NetReturn);
      }
      if (asset.emissionCurrencyISO === "USD") {
        totalNetReturnOneValue += parseFloat(netReturnUSD);
      } else {
        totalNetReturnOneValue += parseFloat(asset.NetReturn);
      }

      totalAbsoluteReturn += parseFloat(asset.TRT);
      // totalYTM += parseFloat(asset.YTM);
      totalPortfolio += (+asset.totalUSDWithCoupon * 100) / +portfolioTotal;
    });

    // totalYTM = totalYTM / subAssetClass.asset.length;

    subAssetClass.asset.forEach((asset) => {
      const isMutualFunds = asset.form === "Mutual Funds";
      const isPYG = asset.emissionCurrencyISO === "PYG";
      const totalValue = isPYG
        ? parseFloat(asset.totalUSDWithCoupon)
        : parseFloat(asset.totalOriginCurrencyWithCoupon);

      if (isMutualFunds) {
        totalYTM += parseFloat(asset.FundAnnualVariation);
      } else {
        totalYTM += parseFloat(asset.YTM) * (totalValue / totalMarketValue);
      }
    });

    setTotalMarketValue(totalMarketValue);
    setTotalMarketValueOneValue(totalMarketValueOneValue);
    setTotalInvestmentCost(totalInvestmentCost);
    setTotalInvestmentCostOneValue(totalInvestmentCostOneValue);
    setTotalAbsoluteReturn(totalAbsoluteReturn);
    setTotalNetReturn(totalNetReturn);
    setTotalNetReturnOneValue(totalNetReturnOneValue);
    setTotalYTM(totalYTM);
    setTotalPortfolio(totalPortfolio);
  }, []);

  const toggleSell = (asset, total) => {
    setSell((prev) => (prev.open ? { open: false } : { open: true, asset, total }));
  };

  return (
    <div className={style.subAssetClassDisplay}>
      <Row>
        <Col xs={12} className={style.titleWrapper}>
          <span className={style.iconWrapper} onClick={toggle}>
            <Icons.ArrowLeftIcon className={style.icon} />
          </span>
          <span className={style.title}>{subAssetClass.name}</span>
        </Col>
        <hr className={style.hrTitle} />
      </Row>
      <Row>
        <Col>
          <Container className={style.summary}>
            <Row>
              <Col xs={12} lg={6} xl={8} className={style.centeredCol}>
                <Row className={style.centeredRow}>
                  <Col className={style.item} xs={12} sm={6} xl={4}>
                    <span>Valor de mercado</span>
                    <span className={style.label}>
                      <span className={style.currency}> {activeCurrency !== "PYG" ? "USD" : "PYG"}</span>
                      <FormattedNumber
                        value={activeCurrency === "ALL" ? totalMarketValue : totalMarketValueOneValue}
                        minimumFractionDigits={subAssetClass.asset[0].emissionCurrencyISO === "PYG" ? 0 : 2}
                        maximumFractionDigits={subAssetClass.asset[0].emissionCurrencyISO === "PYG" ? 0 : 2}
                      />
                    </span>
                  </Col>
                  {!isSubAssetFund && (
                    <Col className={style.item} xs={12} sm={6} xl={4}>
                      <span>Rendimiento (TIR)</span>
                      <span className={style.label}>
                        <FormattedNumber
                          value={totalYTM}
                          minimumFractionDigits={2}
                          maximumFractionDigits={2}
                        />
                        %
                      </span>
                    </Col>
                  )}
                  <Col className={style.item} xs={12} sm={6} xl={4}>
                    <span>Portafolio</span>
                    <span className={style.label}>
                      <FormattedNumber
                        value={totalPortfolio}
                        minimumFractionDigits={2}
                        maximumFractionDigits={2}
                      />
                      %
                    </span>
                  </Col>
                </Row>
              </Col>
              <Col className={style.risk} xs={12} lg={6} xl={4}>
                <span className={style.titleRisk}>Calificación de riesgo</span>
                <div className={style.graphWrapper}>
                  <AssetsRisksDoghnut assets={subAssetClass.asset} portfolioTotal={portfolioTotal} />
                  <AssetRiskList assets={subAssetClass.asset} portfolioTotal={portfolioTotal} />
                </div>
              </Col>
            </Row>
          </Container>
          <hr className={style.hrDivider} style={{ width: "auto" }} />
        </Col>
      </Row>
      {subAssetClass.asset.map((Asset) => {
        const investCost = (+Asset.NetReturn - (+Asset.valuetotal + +Asset.realizePL)) * -1;
        const totalValue = parseFloat(Asset.totalOriginCurrencyWithCoupon);

        const handleDownloadAttachment = () => {
          getAssetAttachment({
            accountNumber: currentAccount.AccountNumber,
            assetId: Asset.id,
          })
            .then(({ response }) => {
              if (response.result.Code == 200) {
                const fileName = `${response.AssetAttachmentName.trim()}.${response.AssetAttachmentExtension.trim()}`;
                const element = document.createElement("a");
                element.setAttribute(
                  "href",
                  "data:application/octet-stream;base64," + encodeURIComponent(response.blob)
                );
                element.setAttribute("download", fileName);
                element.style.display = "none";
                document.body.appendChild(element);
                element.click();
                document.body.removeChild(element);
              } else {
                showToast(response.result.Description, true);
              }
            })
            .catch((err) => {
              console.error(err);
              showToast("Error obteniendo el archivo adjunto", true);
            });
        };

        return (
          <Row>
            <Col>
              <Container className={style.assetContainer} style={{ backgroundColor: "#FDFDFC" }}>
                <Row>
                  <Col xs={12} lg={4} className={style.assetInfo}>
                    <span className={`${style.assetField} ${style.name}`}>{Asset.name}</span>
                    <span className={`${style.assetField} ${style.nominal}`}>
                      {Asset.form === "Mutual Funds" ? (
                        <span className={style.label}>Total Efectivo: </span>
                      ) : (
                        <span className={style.label}>Valor Nominal: </span>
                      )}

                      <span className={style.currency}>
                        {Asset.emissionCurrencyISO === ""
                          ? Asset.emissionCurrencyId === "0"
                            ? "PYG"
                            : "USD"
                          : Asset.emissionCurrencyISO}
                      </span>

                      {Asset.form !== "Mutual Funds" ? (
                        <FormattedNumber
                          value={Asset.parvalue * Asset.nominalValue}
                          minimumFractionDigits={
                            Asset.emissionCurrencyISO === "PYG" || Asset.emissionCurrencyId === "0" ? 0 : 2
                          }
                          maximumFractionDigits={
                            Asset.emissionCurrencyISO === "PYG" || Asset.emissionCurrencyId === "0" ? 0 : 2
                          }
                        />
                      ) : (
                        <FormattedNumber
                          value={Asset.totalOriginCurrencyWithCoupon}
                          minimumFractionDigits={
                            Asset.emissionCurrencyISO === "PYG" || Asset.emissionCurrencyId === "0" ? 0 : 2
                          }
                          maximumFractionDigits={
                            Asset.emissionCurrencyISO === "PYG" || Asset.emissionCurrencyId === "0" ? 0 : 2
                          }
                        />
                      )}
                    </span>
                    <span className={style.assetField}>
                      <span className={style.label}>ISIN: </span>
                      {Asset.isin}
                    </span>
                    {Asset.form !== "Mutual Funds" ? (
                      <React.Fragment>
                        <span className={style.assetField}>
                          <span className={style.label}>Calificación de riesgo: </span>
                          {Asset.riskType}
                        </span>
                        <span className={style.assetField}>
                          <span className={style.label}>Tasa cupón: </span>
                          <FormattedNumber
                            value={Asset.paymentRateFixed}
                            minimumFractionDigits={2}
                            maximumFractionDigits={2}
                          />
                          <span>%</span>
                        </span>
                      </React.Fragment>
                    ) : (
                      <React.Fragment />
                    )}
                  </Col>
                  <Col xs={12} lg={5} className={`${style.assetInfo} ${style.secondCol}`}>
                    <span className={style.assetField}>
                      <span className={style.label}>{`${
                        Asset.form !== "Mutual Funds" ? "Cantidad" : "Cuotapartes"
                      }: `}</span>

                      {Asset.form !== "Mutual Funds" ? (
                        <FormattedNumber
                          value={Asset.nominalValue}
                          minimumFractionDigits={0}
                          maximumFractionDigits={0}
                        />
                      ) : (
                        <FormattedNumber
                          value={Asset.nominalValueExtended}
                          minimumFractionDigits={6}
                          maximumFractionDigits={6}
                        />
                      )}
                    </span>
                    {Asset.AssetAnnualTargetPerformance && Asset.AssetAnnualTargetPerformance != 0 && (
                      <span className={style.assetField}>
                        <span className={style.label}>{`Rendimiento Objetivo Anual: `}</span>
                        <FormattedNumber
                          value={Asset.AssetAnnualTargetPerformance}
                          minimumFractionDigits={2}
                          maximumFractionDigits={2}
                        />
                        <span className={style.label}> %</span>
                      </span>
                    )}
                    <span className={style.assetField}>
                      {Asset.form !== "Mutual Funds" ? (
                        <React.Fragment>
                          <span className={style.label}>Rendimiento (TIR): </span>
                          <FormattedNumber
                            value={Asset.YTM}
                            minimumFractionDigits={2}
                            maximumFractionDigits={2}
                          />
                          <span>%</span>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          {/* <span className={style.label}>Rendimiento: </span>
                          <FormattedNumber
                            value={Asset.FundAnnualVariation}
                            minimumFractionDigits={2}
                            maximumFractionDigits={6}
                          />
                          <span>%</span> */}
                        </React.Fragment>
                      )}
                    </span>
                  </Col>
                  <Col xs={12} lg={3} className={`${style.assetInfo} ${style.thirdCol}`}>
                    {Asset.form === "Bonds" && (
                      <div className={style.totalValueWrapper}>
                        <span className={style.value}>
                          <span className={style.currency}>
                            {Asset.emissionCurrencyISO === ""
                              ? Asset.emissionCurrencyId === "0"
                                ? "PYG"
                                : "USD"
                              : Asset.emissionCurrencyISO}
                          </span>
                          <FormattedNumber
                            value={totalValue}
                            minimumFractionDigits={
                              Asset.emissionCurrencyISO === "PYG" || Asset.emissionCurrencyId === "0" ? 0 : 2
                            }
                            maximumFractionDigits={
                              Asset.emissionCurrencyISO === "PYG" || Asset.emissionCurrencyId === "0" ? 0 : 2
                            }
                          />
                        </span>
                        <span className={style.label}>Valor de mercado</span>
                      </div>
                    )}
                    {subAssetClass.cda && (
                      <Button className={`${style.button} ${style.show}`} onClick={handleDownloadAttachment}>
                        Mostrar título de CDA <Icons.DocumentIcon size="sm" className={style.icon} />
                      </Button>
                    )}
                    {Asset.form !== "Mutual Funds" &&
                      isActionAllowed(FEATURE.SELL_REQUEST, currentAccount) && (
                        <Button
                          className={style.button}
                          onClick={() => {
                            toggleSell(Asset, Asset.nominalValue);
                          }}
                        >
                          Pedir cotización
                        </Button>
                      )}
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
        );
      })}
      {sell.open && <SellModal open={sell.open} asset={sell.asset} total={sell.total} toggle={toggleSell} />}
    </div>
  );
};

const SellModal = ({ open, asset, total, toggle }) => {
  const totalSell = <FormattedNumber value={total} minimumFractionDigits={0} maximumFractionDigits={0} />;
  const [loading, setLoading] = useState(false);
  const [qty, setQty] = useState(Math.round(1));
  const [error, setError] = useState("");
  const [activeAccount, setActiveAccount] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingAccounts, setloadingAccounts] = useState(false);
  const [noFunds, setnoFunds] = useState(false);
  const [allBankAccountsAreInvalid, setAllBankAccountsAreInvalid] = useState(false);


  const currentAccount = useSelector(getCurrentAccount);
  const personInfo = useSelector(getSavedPersonInfo);

  useEffect(() => {
    if (personInfo.PersonBankAccount) {
      setBankAccounts(personInfo.PersonBankAccount);
    }
  }, [personInfo]);

  const onConfirm = () => {
    if (+qty > +total) setError("La cantidad ingresada no puede ser mayor a la cantidad del portafolio.");
    else {
      setLoading(() => true);
      setError("");
      const Order = {
        AssetId: asset.id,
        OrderHierarchy: "Parent",
        cashdiffered: true,
        CurrentAccount: {
          AccountId: currentAccount.id,
          Quantity: qty,
          clientBankAccount: activeAccount.PersonBankAccountNumber,
        },
      };
      askPrice({ Order })
        .then(({ response }) => {
          if (response.result.Code !== 200) setError("Error enviando tu pedido de cotización.");
          else {
            showToast("Enviamos tu pedido de cotización.");
            toggle();
          }
        })
        .catch((err) => console.error(err))
        .finally(() => {
          setLoading(() => false);
        });
    }
  };

  const handleOnChangeQty = (e) => {
    setQty(+e.target.value);
  };

  const setAllBankAccountAreInvalid  = (allAreInvalid, notEnoughFunds) => {
    setnoFunds(notEnoughFunds || noFunds)
    setAllBankAccountsAreInvalid(() => (allAreInvalid ? true : false));
  };

  const setLoadingStates = (isLoading) => {
   setloadingAccounts(isLoading)
  };

  const NoBankAccountsAvailable = () => {
        return (
          <div style={{ textAlign: "center", marginTop: "15px" }}>
            <span style={{ fontSize: "16px", fontWeight: "bold" }}>
              No tienes cuentas de banco disponibles
            </span>
          </div>
        );
  };

  return (
    <Modal isOpen={open} className={style.modalWrapper}>
      <Container className={style.sellContainer}>
        <span className={style.closeIcon} onClick={toggle}>
          <Icons.Close />
        </span>
        <span className={style.sellAssetName}>{asset.name}</span>
        <span className={style.sellTitle}>¿Cuántos querés cotizar?</span>
        <hr className={style.divider} />
        <span className={style.qtyLabel}>Cantidad</span>
        <input
          className={style.qtyInput}
          placeholder="Cantidad a vender"
          type="number"
          min={0}
          max={total}
          value={qty}
          onChange={handleOnChangeQty}
        />
        <span className={style.maxQty}>Cantidad disponible: {totalSell}</span>
        {
          !loadingAccounts && allBankAccountsAreInvalid && (
          (noFunds) ? 
            <div className={style.invalidInput}>Acreditá saldo en tu cuenta para continuar.</div> :
            <div className={style.invalidInput}>No tenés una cuenta para realizar la operación.</div>
          )
        }
        <div className={style.bankAccounts}>
          <p className={style.title}>Elegí una cuenta para la acreditación</p>
          {!bankAccounts.filter((account) => account.PersonBankAccountStatus === "Active").length  ? 
          (<NoBankAccountsAvailable />) : (
          <BankAccountsSelector
            bankAccounts={bankAccounts}
            selectedBankAccount={activeAccount}
            onSelectBankAccount={setActiveAccount}
            setAllBankAccountAreInvalid={setAllBankAccountAreInvalid}
            setLoadingStates = {setLoadingStates}
            filters={{
              currencyISO: asset.emissionCurrencyISO,
              mode: modes.SELL,
            }}
          /> )}
        </div>
        {loading ? (
          <CircularProgress />
        ) : (
          <Button className={style.buttonSell} onClick={onConfirm} disabled={!activeAccount}>
            Pedir cotización
          </Button>
        )}

        <span className={style.disclaimer}>
          La cotización estará disponible en la sección “Autorizaciones” dentro de las 24 horas hábiles
          posteriores al pedido.
        </span>
        {error && <span className={style.error}>{error}</span>}
      </Container>
    </Modal>
  );
};
